/**
 * ShortcutCheatsheet — the `?` dialog every keyboard-driven ERP grows.
 *
 * Renders every registered shortcut (static app-wide + dynamic per-page) in a
 * two-column list grouped by section. Keys are rendered through `<Kbd>` and
 * chord sequences show as two boxed strokes with "then" between them so the
 * operator can see at a glance that they need to release the first key.
 */
import { useMemo } from 'react';
import { KeyboardIcon } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Kbd } from '@/shared/ui/kbd';
import { isMacPlatform } from '@/shared/hooks/use-hotkey';
import {
  useAllShortcuts,
  type ShortcutGroup,
  type ShortcutSpec,
} from './shortcut-registry';

/* -------------------------------------------------------------------------- */
/*  Group order + labels                                                      */
/* -------------------------------------------------------------------------- */

const GROUP_ORDER: ShortcutGroup[] = [
  'Global',
  'Navigation',
  'List page',
  'Form',
  'Detail page',
];

/* -------------------------------------------------------------------------- */
/*  Key rendering                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Render the list of stroke-tokens for a shortcut. A chord ("g d") comes back
 * as `[["G"], ["D"]]` so we can drop a "then" separator between them.
 */
function tokenise(keys: string): string[][] {
  const strokes = keys.trim().includes(' ') && !/\+\s/.test(keys.trim())
    ? keys.trim().split(/\s+/)
    : [keys.trim()];
  return strokes.map((s) => strokeTokens(s));
}

function strokeTokens(stroke: string): string[] {
  const isMac = isMacPlatform();
  const parts = stroke.split('+').map((p) => p.trim().toLowerCase()).filter(Boolean);
  const modifiers: string[] = [];
  let key = '';
  for (const p of parts) {
    if (p === 'ctrl' || p === 'cmd' || p === 'mod' || p === 'control') {
      modifiers.push(isMac ? '⌘' : 'Ctrl');
    } else if (p === 'meta' || p === 'super' || p === 'win') {
      modifiers.push('⌘');
    } else if (p === 'shift') {
      modifiers.push(isMac ? '⇧' : 'Shift');
    } else if (p === 'alt' || p === 'option' || p === 'opt') {
      modifiers.push(isMac ? '⌥' : 'Alt');
    } else {
      key = p;
    }
  }
  return [...modifiers, renderKeyToken(key)];
}

function renderKeyToken(k: string): string {
  if (!k) return '';
  if (k === 'escape') return 'Esc';
  if (k === 'enter') return 'Enter';
  if (k === 'delete') return 'Del';
  if (k === 'space') return 'Space';
  if (k === 'arrowleft') return '←';
  if (k === 'arrowright') return '→';
  if (k === 'arrowup') return '↑';
  if (k === 'arrowdown') return '↓';
  if (k.length === 1) return k.toUpperCase();
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/* -------------------------------------------------------------------------- */
/*  Dialog                                                                    */
/* -------------------------------------------------------------------------- */

export interface ShortcutCheatsheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShortcutCheatsheet({ open, onOpenChange }: ShortcutCheatsheetProps) {
  const shortcuts = useAllShortcuts();
  const grouped = useMemo(() => {
    const map = new Map<ShortcutGroup, ShortcutSpec[]>();
    for (const s of shortcuts) {
      const list = map.get(s.group);
      if (list) list.push(s);
      else map.set(s.group, [s]);
    }
    // Order groups deterministically; anything unknown goes last.
    return [
      ...GROUP_ORDER.filter((g) => map.has(g)).map(
        (g) => [g, map.get(g)!] as const,
      ),
      ...Array.from(map.entries()).filter(
        ([g]) => !GROUP_ORDER.includes(g),
      ),
    ];
  }, [shortcuts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyboardIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
            Keyboard shortcuts
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {grouped.map(([group, items]) => (
              <section key={group} className="space-y-2">
                <h3 className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                  {group}
                </h3>
                <ul className="divide-y divide-border rounded-md border border-border bg-card">
                  {items.map((s, i) => (
                    <li
                      key={`${s.keys}-${s.label}-${i}`}
                      className="flex items-center justify-between gap-4 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground">{s.label}</p>
                        {s.description && (
                          <p className="truncate text-xs text-muted-foreground">
                            {s.description}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {tokenise(s.keys).map((tokens, si) => (
                          <span key={si} className="flex items-center gap-0.5">
                            {si > 0 && (
                              <span className="mx-1 text-2xs text-muted-foreground">
                                then
                              </span>
                            )}
                            {tokens.map((t, ti) => (
                              <Kbd key={ti}>{t}</Kbd>
                            ))}
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Text-field strokes are ignored unless they use a modifier (Ctrl, Shift, Alt).
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
